<template>
  <b-row
    class="dropzone"
    :class="{ dragging: isDragging, processing: isProcessingImages }"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <div
      v-if="isProcessingImages"
      class="upload-loading-overlay"
    >
      <div class="upload-loading-card">
        <div class="upload-spinner" />
        <strong>Compactando imagens...</strong>
        <span>{{ uploadLoadingText }}</span>
      </div>
    </div>

    <b-col
      cols="12"
      md="12"
      class="d-flex justify-content-center align-items-center"
    >
      {{ uploadStatusText }}
    </b-col>
    <b-col
      cols="12"
      md="12"
      class="d-flex justify-content-center"
    >
      <div class="upload-warning">
        <strong>Atenção:</strong> imagens grandes são compactadas automaticamente antes do upload.
        Se ainda ultrapassarem {{ maxImageSizeMb }}MB após a compressão, você será avisado.
      </div>
    </b-col>
    <b-col
      cols="12"
      md="12"
      class="d-flex justify-content-center align-items-center"
    >
      <label
        for="input"
        class="file-input-label label-custom"
        :class="{ dragging: isDragging, disabled: isProcessingImages }"
      >
        <i class="fas fa-cloud-upload-alt" />
        <span class="dropzone-text">Arraste e solte as imagens aqui ou clique para selecionar</span>
      </label>
    </b-col>

    <b-col
      cols="12"
      md="12"
      class="align-images"
    >
      <input
        id="input"
        ref="input"
        hidden
        type="file"
        multiple
        :disabled="isProcessingImages"
        @change="handleFileUpload"
        @drop.prevent="handleFileUpload"
      >
      <div
        v-for="(image, index) in imagensItemData"
        :key="index"
        class="image-container"
        :class="{ dragging: isDragging }"
      >
        <img
          v-if="isImageUrl(image)"
          class="image"
          :src="image.caminho_imagem"
        >
        <img
          v-else-if="image && image.base64 !== null"
          class="image"
          :src="image.base64 || ''"
        >
        <button
          class="remove-button"
          :disabled="isProcessingImages"
          @click.prevent="removeImage(index)"
        >
          x
        </button>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { BRow, BCol } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow,
    BCol,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        id: null,
        nome: '',
        descricao: '',
        id_categoria: null,
        id_tabela_preco: null,
        imagens: [],
      }),
    },
  },
  data() {
    const maxSizeEnv = Number(process.env.VUE_APP_IMAGE_MAX_SIZE_MB)
    const maxImageSizeMb = Number.isFinite(maxSizeEnv) && maxSizeEnv > 0 ? maxSizeEnv : 5

    return {
      maxImageSizeMb,
      maxImageSize: maxImageSizeMb * 1024 * 1024,
      maxImageDimension: 1920,
      minImageDimension: 900,
      compressionQuality: 0.86,
      minCompressionQuality: 0.65,
      compressionDimensionStep: 0.85,
      allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
      imagensToAWS: [],
      isDragging: false,
      processingImagesCount: 0,
    }
  },
  computed: {
    itemData: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    imagensItemData() {
      return this.$store.getters['appAdmProdutos/GET_IMAGENS_ITEM']
    },
    isProcessingImages() {
      return this.processingImagesCount > 0
    },
    uploadStatusText() {
      if (this.isProcessingImages) {
        return `${this.processingImagesCount} ${this.processingImagesCount === 1 ? 'imagem sendo compactada' : 'imagens sendo compactadas'}...`
      }

      const imageCount = this.imagensItemData.length
      return imageCount > 0 ? `${imageCount} Imagens selecionadas` : 'Nenhuma imagem selecionada'
    },
    uploadLoadingText() {
      return this.processingImagesCount === 1
        ? 'Aguarde enquanto preparamos a imagem para envio.'
        : `Aguarde enquanto preparamos ${this.processingImagesCount} imagens para envio.`
    },
  },
  methods: {
    isImageUrl(image) {
      return typeof image?.caminho_imagem !== 'undefined'
    },
    showFileWarning(title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          icon: 'AlertTriangleIcon',
          variant: 'warning',
        },
      })
    },
    validateImageType(file) {
      if (!this.allowedImageTypes.includes(file.type)) {
        this.showFileWarning(
          'Tipo de arquivo inválido',
          'Selecione apenas imagens JPEG, PNG ou GIF.',
        )
        return false
      }

      return true
    },
    loadImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        const objectUrl = URL.createObjectURL(file)

        image.onload = () => {
          URL.revokeObjectURL(objectUrl)
          resolve(image)
        }

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error('Não foi possível ler a imagem selecionada.'))
        }

        image.src = objectUrl
      })
    },
    canvasToFile(canvas, file, mimeType, quality) {
      return new Promise(resolve => {
        canvas.toBlob(blob => {
          if (!blob) {
            resolve(file)
            return
          }

          resolve(new File([blob], file.name, {
            type: mimeType,
            lastModified: file.lastModified,
          }))
        }, mimeType, quality)
      })
    },
    getCompressedFileName(file, mimeType) {
      if (mimeType !== 'image/jpeg') {
        return file.name
      }

      const nameWithoutExtension = file.name.includes('.') ? file.name.replace(/\.[^.]+$/, '') : file.name
      return `${nameWithoutExtension}.jpg`
    },
    async compressImage(file) {
      if (file.type === 'image/gif') {
        return file
      }

      const image = await this.loadImage(file)
      const needsCompress = file.size > this.maxImageSize
      const needsResize = image.width > this.maxImageDimension || image.height > this.maxImageDimension

      if (!needsResize && !needsCompress) {
        return file
      }

      let targetDimension = this.maxImageDimension
      let bestFile = file

      while (targetDimension >= this.minImageDimension) {
        const scale = Math.min(
          targetDimension / image.width,
          targetDimension / image.height,
          1,
        )
        const width = Math.max(1, Math.round(image.width * scale))
        const height = Math.max(1, Math.round(image.height * scale))
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const context = canvas.getContext('2d')
        context.fillStyle = '#fff'
        context.fillRect(0, 0, width, height)
        context.drawImage(image, 0, 0, width, height)

        const mimeType = 'image/jpeg'
        let quality = this.compressionQuality

        while (quality >= this.minCompressionQuality) {
          // eslint-disable-next-line no-await-in-loop
          const compressedFile = await this.canvasToFile(
            canvas,
            new File([file], this.getCompressedFileName(file, mimeType), {
              type: mimeType,
              lastModified: file.lastModified,
            }),
            mimeType,
            quality,
          )

          if (compressedFile.size < bestFile.size) {
            bestFile = compressedFile
          }

          if (compressedFile.size <= this.maxImageSize) {
            return compressedFile
          }

          quality -= 0.07
        }

        targetDimension = Math.floor(targetDimension * this.compressionDimensionStep)
      }

      return bestFile
    },
    async prepareImage(file) {
      if (!this.validateImageType(file)) {
        return null
      }

      let compressedFile

      try {
        compressedFile = await this.compressImage(file)
      } catch (error) {
        this.showFileWarning(
          'Não foi possível compactar',
          `A imagem ${file.name} não pode ser processada automaticamente. Tente enviar outro arquivo.`,
        )
        return null
      }

      if (compressedFile.size > this.maxImageSize) {
        this.showFileWarning(
          'Arquivo muito grande',
          `A imagem ${file.name} ainda ficou acima de ${this.maxImageSizeMb}MB após a compressão automática. Reduza a resolução ou envie uma versão menor.`,
        )
        return null
      }

      return compressedFile
    },
    readFileAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = e => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Não foi possível processar a imagem selecionada.'))
        reader.readAsDataURL(file)
      })
    },
    base64ToBlob(dataURI) {
      const byteString = atob(dataURI.base64.split(',')[1])
      const type = dataURI.base64.split(',')[0].split(':')[1].split(';')[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i)
      }
      return new File([ab], dataURI.name, { type })
    },
    handleTranformImages() {
      this.imagensToAWS = []
      for (let i = 0; i < this.itemData.imagens.length; i += 1) {
        if (this.itemData.imagens[i].base64 !== null) {
          const blob = this.base64ToBlob(this.itemData.imagens[i])
          this.imagensToAWS.push(blob)
        }
      }
    },
    handleDrop(event) {
      event.preventDefault()
      this.isDragging = false
      const { files } = event.dataTransfer
      this.handleTranformImages()
      this.handleFiles(files)
    },
    handleDragOver(event) {
      event.preventDefault()
      this.isDragging = true
    },
    handleDragLeave(event) {
      event.preventDefault()
      this.isDragging = false
    },
    handleFileUpload() {
      const { files } = this.$refs.input
      this.handleTranformImages()
      this.handleFiles(files)
    },
    handleFileDrop(event) {
      const { files } = event.dataTransfer
      this.handleTranformImages()
      this.handleFiles(files)
    },
    async handleFiles(files) {
      const selectedFiles = Array.from(files)

      if (selectedFiles.length === 0) {
        return
      }

      this.processingImagesCount += selectedFiles.length
      this.$store.commit('appAdmProdutos/SET_IS_PROCESSING_IMAGES', true)

      try {
        await Promise.all(selectedFiles.map(async file => {
          const preparedFile = await this.prepareImage(file)

          if (!preparedFile) {
            return
          }

          const base64 = await this.readFileAsDataURL(preparedFile)

          this.$store.commit('appAdmProdutos/SET_IMAGE_PRODUTO_DATA', {
            name: preparedFile.name,
            file: preparedFile,
            base64,
          })
        }))
      } finally {
        this.processingImagesCount = Math.max(0, this.processingImagesCount - selectedFiles.length)

        if (this.processingImagesCount === 0) {
          this.$store.commit('appAdmProdutos/SET_IS_PROCESSING_IMAGES', false)
        }
      }
    },
    removeImage(index) {
      const img = this.itemData.imagens[index]
      if (img && (img.id || img.imagem_key)) {
        this.$store.dispatch('appAdmItemImagem/deleteItemImagem', { itemImagen: img })
      }
      this.$store.commit('appAdmProdutos/REMOVE_IMAGE_PRODUTO_DATA', img)
    },
  },
}
</script>

<style scoped>
.align-images {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

.dropzone {
  position: relative;
}

.dropzone.processing {
  min-height: 260px;
}

.upload-loading-overlay {
  position: absolute;
  z-index: 10;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(2px);
}

.upload-loading-card {
  min-width: 280px;
  max-width: 420px;
  padding: 22px 24px;
  border-radius: 8px;
  border: 1px solid #d8deea;
  background: #fff;
  box-shadow: 0 12px 28px rgba(34, 41, 47, 0.14);
  color: #344054;
  text-align: center;
}

.upload-loading-card strong {
  display: block;
  margin-bottom: 6px;
  font-size: 16px;
}

.upload-loading-card span {
  display: block;
  color: #667085;
  font-size: 13px;
  line-height: 1.4;
}

.upload-spinner {
  width: 42px;
  height: 42px;
  margin: 0 auto 14px;
  border: 4px solid #e6ebf5;
  border-top-color: #7367f0;
  border-radius: 50%;
  animation: upload-spin 0.85s linear infinite;
}

@keyframes upload-spin {
  to {
    transform: rotate(360deg);
  }
}

.upload-warning {
  width: 84%;
  margin: 2px 0 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ffd48a;
  background: #fff7e8;
  color: #8a5a00;
  font-size: 13px;
  line-height: 1.4;
}

.label-custom {
  border: 2px dashed blue;
  width: 84%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 92px;
}

.file-input-label {
  display: inline-block;
  margin: 20px 10px;
  padding: 10px;
  background-color: #eee;
  border-radius: 5px;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.dropzone-text {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: #333;
  margin-bottom: 10px;
}
.file-input-label:hover {
  background-color: #ddd;
}

.file-input-label.disabled {
  cursor: wait;
  opacity: 0.7;
}

.file-input-label i {
  margin-right: 10px;
}

.image-container {
  position: relative;
  display: inline-block;
  margin: 10px;
}

.image {
  width: 150px;
  height: 150px;
  object-fit: cover;
  border-radius: 5px;
}

.remove-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  font-size: 20px;
  color: #555;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
}

.remove-button:hover {
  background-color: #ffaaaa;
  color: #fff;
}

.remove-button:disabled {
  cursor: wait;
  opacity: 0.6;
}

/* Estilos adicionais para o drag and drop */
.file-input-label.dragging{
  background-color: #85ff4d;
  border: 3px dashed #00967e;
}

.file-input-label.dragging i {
  color: #333;
}
</style>
